import LFCChartsHeatMap from '_components/charts/LFCChartsHeatMap';
import {ProgressAria} from '_components/ProgressAria';
import LFCSingleValues from '_components/surfaces/LFCSingleValues';
import LFCTitledCard from '_components/surfaces/LFCTitledCard';
import {rendTotalJudgeDisp} from '_logics/LFCRenderUtil';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import {DetailDialog} from 'pages/l-qualify/inspectionresults/components/detail-dialog';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';

import {RELOAD_TIME} from '../';
import {SearchConditions} from '../types';

interface LastExecutionResultPanelProps {
  searchConditions: SearchConditions | null;
  setDelay: React.Dispatch<React.SetStateAction<number | null>>;
}

const LastExecutionResultPanel: React.FC<LastExecutionResultPanelProps> = ({
  searchConditions,
  setDelay
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const [data60008, setData60008] = useState({
    deviceid: '',
    dongleid: '',
    lq_time: '',
    serial: '',
    total_judge: 0,
    uuid: '',
    work: ''
  });
  const [data60347, setData60347] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [openUuid, setOpenUuid] = React.useState('');

  /**
   * 明細選択
   */
  const onClickHeat = (_params: {x: string; y: string}) => {
    setDelay(null); // 再検索を停止
    setOpenUuid(data60008.uuid);
    setOpen(true);
  };

  /**
   * 明細Pop閉じる
   */
  const onClosekHeat = () => {
    setDelay(RELOAD_TIME); // 再検索再開
    setOpen(false);
    setOpenUuid('');
  };

  /**
   * 初期処理
   */
  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  /**
   * 検索処理
   */
  useEffect(() => {
    if (searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    const parameters = {
      work: !isEmpty(searchConditions.work) ? '{' + searchConditions.work.join(',') + '}' : null,
      deviceid: !isEmpty(searchConditions.deviceid)
        ? '{' + searchConditions.deviceid.join(',') + '}'
        : null,
      dongleid: !isEmpty(searchConditions.dongleid)
        ? '{' + searchConditions.dongleid.join(',') + '}'
        : null
    };

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 60008,
        parameters,
        name: `${t('最終実行結果')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then(datas => {
        setData60008(datas.length > 0 ? datas[0] : []);
      }),
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 60347,
        parameters,
        ds_state: setData60347,
        name: `${t('最終実行結果の部位別結果')}`,
        cancelToken: cancelSource.current.token,
        t
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [searchConditions, enqueueSnackbar, t]);

  return (
    <ProgressAria startProcess={startProcess}>
      <LFCTitledCard title={t('最終実行結果')} transparent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <LFCSingleValues
              columns={[
                {
                  field: 'total_judge',
                  headerName: t('総合判定'),
                  formatter: rendTotalJudgeDisp
                },
                {field: 'lq_time', headerName: t('日時')},
                {field: 'serial', headerName: t('シリアルNo')},
                {field: 'work', headerName: t('機種')},
                {field: 'deviceid', headerName: t('マシン')},
                {field: 'dongleid', headerName: t('オペレータ')}
              ]}
              source={data60008}
            />
          </Grid>
          <Grid item xs={12}>
            <LFCChartsHeatMap
              title={`${t('判定結果')}(${t('ジョブ')}×${t('部位')})`}
              source={data60347}
              x={{dsColumn: 'map'}}
              y={{dsColumn: 'job_type_name'}}
              series="judge"
              visual_map={{
                min: 0,
                max: 1,
                show: false
              }}
              exportData={data60347}
              exportFields={{
                job_type_name: t('ジョブ'),
                map: t('部位'),
                judge: t('判定')
              }}
              exportFilename={`${t('部位別結果')}`}
              onClick={onClickHeat}
            />
            <DetailDialog
              key={openUuid}
              open={open}
              onClose={() => onClosekHeat()}
              uuid={openUuid}
            />
          </Grid>
        </Grid>
      </LFCTitledCard>
    </ProgressAria>
  );
};

export default LastExecutionResultPanel;
